import { supabase } from '../lib/supabase';

export interface MatchOfficial {
  id: string;
  match_id: string;
  official_id: string;
  role: 'referee' | 'assistant1' | 'assistant2' | 'var' | 'var_assistant' | 'fourth';
  official: {
    id: string;
    name: string;
    certification_level: string;
    date_of_birth: string;
    avatar: string;
    experience_years: number;
    specializations: string[];
    contact_info: string;
  };
}

export interface MatchDetails {
  id: string;
  name: string;
  start_date: string;
  end_date: string | null;
  venue: string;
  home_team_id: string;
  away_team_id: string;
  tournament_id: string;
  season_id?: string;
  home_score: number;
  away_score: number;
  home_penalty_score?: number;
  away_penalty_score?: number;
  status: 'scheduled' | 'live' | 'finished';
  created_at: string;
  updated_at: string;
  home_team: {
    id: string;
    name: string;
    logo: string;
    flag: string;
    city: string;
    venue: string;
    date_established: string;
    website: string;
    social_media: string;
  };
  away_team: {
    id: string;
    name: string;
    logo: string;
    flag: string;
    city: string;
    venue: string;
    date_established: string;
    website: string;
    social_media: string;
  };
  tournament: {
    id: string;
    name: string;
    logo: string;
    country: string;
  };
  season?: {
    id: string;
    name: string;
    start_date?: string;
    end_date?: string;
  };
  match_officials: MatchOfficial[];
}

export const matchDetailsService = {
  // Get match details by ID
  async getMatchDetails(matchId: string): Promise<MatchDetails | null> {
    try {
      const { data, error } = await supabase
        .from('matches')
        .select(`
          *,
          home_team:teams!home_team_id(*),
          away_team:teams!away_team_id(*),
          tournament:tournaments(*),
          season:seasons(*),
          match_officials:match_officials(
            *,
            official:officials(*)
          )
        `)
        .eq('id', matchId)
        .single();

      if (error) {
        console.error('Error fetching match details:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getMatchDetails:', error);
      return null;
    }
  },

  // Update match details
  async updateMatchDetails(matchId: string, updates: Partial<MatchDetails>): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('matches')
        .update(updates)
        .eq('id', matchId);

      if (error) {
        console.error('Error updating match details:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateMatchDetails:', error);
      return false;
    }
  },

  // Update match officials
  async updateMatchOfficials(matchId: string, officials: Partial<MatchOfficial>[]): Promise<boolean> {
    try {
      // First, delete existing officials for this match
      const { error: deleteError } = await supabase
        .from('match_officials')
        .delete()
        .eq('match_id', matchId);

      if (deleteError) {
        console.error('Error deleting existing officials:', deleteError);
        return false;
      }

      // Then insert new officials
      const { error: insertError } = await supabase
        .from('match_officials')
        .insert(officials);

      if (insertError) {
        console.error('Error inserting new officials:', insertError);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateMatchOfficials:', error);
      return false;
    }
  }
};
